// src/pages/ResetPasswordScreen.jsx

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Link,
  useLocation,
  useNavigate,
} from "react-router-dom";

import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  Eye,
  EyeOff,
  KeyRound,
  LoaderCircle,
  LockKeyhole,
  ShieldCheck,
  Sparkles,
} from "lucide-react";

import "../sass/pages/resetPassword.scss";

import Logo from "../assets/logo.webp";
import { API_BASE_URL } from "../config/api";
import { useToast } from "../context/ToastContext";

const API_BASE = String(
  API_BASE_URL ||
    process.env.REACT_APP_API_URL ||
    "https://api.psyassist.com.br"
).replace(/\/+$/, "");

const ENDPOINT_RESET_PASSWORD =
  `${API_BASE}/psy/user/reset-password`;

const RESET_STORAGE_KEYS = {
  EMAIL: "psy_password_reset_email",
  LEGACY_EMAIL: "reset_email",
  REQUESTED_AT: "psy_password_reset_requested_at",
  VERIFIED_AT: "psy_password_reset_verified_at",
  RESET_TOKEN: "psy_password_reset_token",
};

const PASSWORD_RULES = [
  {
    key: "length",
    label: "Pelo menos 6 caracteres",
    validate: (value) => value.length >= 6,
  },
  {
    key: "mixed",
    label: "Letras e números deixam a senha mais forte",
    validate: (value) =>
      /[A-Za-zÀ-ÿ]/.test(value) && /\d/.test(value),
  },
  {
    key: "strongLength",
    label: "10 ou mais caracteres aumentam a proteção",
    validate: (value) => value.length >= 10,
  },
];

const readStorage = (storage, key) => {
  try {
    return storage.getItem(key) || "";
  } catch {
    return "";
  }
};

const writeStorage = (
  storage,
  key,
  value
) => {
  try {
    storage.setItem(
      key,
      String(value ?? "")
    );

    return true;
  } catch {
    return false;
  }
};

const removeStorage = (
  storage,
  key
) => {
  try {
    storage.removeItem(key);
  } catch {
    // A limpeza dos demais dados ainda pode continuar.
  }
};

const readApiPayload =
  async (response) => {
    const contentType =
      response.headers.get(
        "content-type"
      ) || "";

    try {
      if (
        contentType.includes(
          "application/json"
        )
      ) {
        const payload =
          await response.json();

        return payload &&
          typeof payload === "object"
          ? payload
          : {};
      }

      const text =
        await response.text();

      return text
        ? {
            message: text,
          }
        : {};
    } catch {
      return {};
    }
  };

const maskEmail = (email = "") => {
  const normalized =
    String(email || "").trim();

  const [localPart, domain] =
    normalized.split("@");

  if (!localPart || !domain) {
    return normalized ||
      "E-mail não identificado";
  }

  const visibleStart =
    localPart.slice(
      0,
      Math.min(2, localPart.length)
    );

  const hiddenLength =
    Math.max(
      localPart.length -
        visibleStart.length,
      3
    );

  return `${visibleStart}${"*".repeat(
    hiddenLength
  )}@${domain}`;
};

const getResetErrorMessage = (
  payload,
  status
) => {
  if (status === 400) {
    return (
      payload?.message ||
      "Os dados enviados para a redefinição não são válidos."
    );
  }

  if (
    status === 401 ||
    status === 403
  ) {
    return (
      payload?.message ||
      "Não foi possível validar os dados da redefinição."
    );
  }

  if (status === 410) {
    return (
      payload?.message ||
      "O fluxo de recuperação expirou. Solicite um novo código."
    );
  }

  if (status === 429) {
    return (
      payload?.message ||
      "Muitas tentativas foram realizadas. Aguarde antes de tentar novamente."
    );
  }

  if (status >= 500) {
    return "O serviço de redefinição está temporariamente indisponível.";
  }

  return (
    payload?.message ||
    "Não foi possível redefinir a senha."
  );
};

const ResetPasswordScreen = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();

  const newPasswordRef =
    useRef(null);

  const [email, setEmail] =
    useState("");

  const [resetToken, setResetToken] =
    useState("");

  const [
    newPassword,
    setNewPassword,
  ] = useState("");

  const [
    confirmPassword,
    setConfirmPassword,
  ] = useState("");

  const [
    showNewPassword,
    setShowNewPassword,
  ] = useState(false);

  const [
    showConfirmPassword,
    setShowConfirmPassword,
  ] = useState(false);

  const [
    capsLockActive,
    setCapsLockActive,
  ] = useState(false);

  const [errors, setErrors] =
    useState({
      newPassword: "",
      confirmPassword: "",
    });

  const [
    isLoading,
    setIsLoading,
  ] = useState(false);

  useEffect(() => {
    const stateEmail = String(
      location.state?.email || ""
    )
      .trim()
      .toLowerCase();

    const storedEmail = String(
      readStorage(
        sessionStorage,
        RESET_STORAGE_KEYS.EMAIL
      ) ||
        readStorage(
          sessionStorage,
          RESET_STORAGE_KEYS.LEGACY_EMAIL
        ) ||
        ""
    )
      .trim()
      .toLowerCase();

    const resolvedEmail =
      stateEmail || storedEmail;

    const stateResetToken = String(
      location.state?.resetToken || ""
    ).trim();

    const storedResetToken = String(
      readStorage(
        sessionStorage,
        RESET_STORAGE_KEYS.RESET_TOKEN
      ) || ""
    ).trim();

    const resolvedResetToken =
      stateResetToken || storedResetToken;

    setEmail(resolvedEmail);
    setResetToken(resolvedResetToken);

    if (resolvedEmail) {
      writeStorage(
        sessionStorage,
        RESET_STORAGE_KEYS.EMAIL,
        resolvedEmail
      );

      writeStorage(
        sessionStorage,
        RESET_STORAGE_KEYS.LEGACY_EMAIL,
        resolvedEmail
      );

      if (resolvedResetToken) {
        writeStorage(
          sessionStorage,
          RESET_STORAGE_KEYS.RESET_TOKEN,
          resolvedResetToken
        );
      }

      window.setTimeout(() => {
        newPasswordRef.current?.focus();
      }, 100);
    }
  }, [location.state]);

  const evaluatedRules =
    useMemo(() => {
      return PASSWORD_RULES.map(
        (rule) => ({
          ...rule,
          valid:
            rule.validate(
              newPassword
            ),
        })
      );
    }, [newPassword]);

  const passwordScore =
    useMemo(() => {
      return evaluatedRules.filter(
        (rule) => rule.valid
      ).length;
    }, [evaluatedRules]);

  const passwordStrengthLabel =
    useMemo(() => {
      if (!newPassword) {
        return "Ainda não informada";
      }

      if (newPassword.length < 6) {
        return "Fraca";
      }

      if (passwordScore < 3) {
        return "Boa";
      }

      return "Forte";
    }, [
      newPassword,
      passwordScore,
    ]);

  const passwordsMatch =
    Boolean(confirmPassword) &&
    newPassword ===
      confirmPassword;

  const hasValidFlow =
    Boolean(email && resetToken);

  const clearFieldError =
    (field) => {
      setErrors((current) => ({
        ...current,
        [field]: "",
      }));
    };

  const clearResetFlow = () => {
    Object.values(
      RESET_STORAGE_KEYS
    ).forEach((key) => {
      removeStorage(
        sessionStorage,
        key
      );

      removeStorage(
        localStorage,
        key
      );
    });
  };

  const validateForm = () => {
    const nextErrors = {
      newPassword: "",
      confirmPassword: "",
    };

    if (!hasValidFlow) {
      toast.warning({
        title:
          "Recuperação expirada",
        message:
          "Valide um novo código antes de redefinir sua senha.",
        duration: 0,
      });

      return false;
    }

    if (!newPassword) {
      nextErrors.newPassword =
        "Informe a nova senha.";
    } else if (
      newPassword.length < 6
    ) {
      nextErrors.newPassword =
        "A senha deve ter pelo menos 6 caracteres.";
    }

    if (!confirmPassword) {
      nextErrors.confirmPassword =
        "Confirme a nova senha.";
    } else if (
      newPassword !==
      confirmPassword
    ) {
      nextErrors.confirmPassword =
        "As senhas não coincidem.";
    }

    setErrors(nextErrors);

    const isValid =
      !nextErrors.newPassword &&
      !nextErrors.confirmPassword;

    if (!isValid) {
      toast.warning({
        title:
          "Revise a nova senha",
        message:
          "Existem campos que precisam da sua atenção.",
      });

      window.setTimeout(() => {
        const firstInvalid =
          document.querySelector(
            ".psy-reset-field--error input"
          );

        firstInvalid?.focus();
      }, 80);
    }

    return isValid;
  };

  const handleSubmit =
    async (event) => {
      event.preventDefault();

      if (isLoading) return;
      if (!validateForm()) {
        return;
      }

      /*
       * A senha deve ser enviada exatamente
       * como foi digitada. Não utilize trim.
       */
      const passwordToSend =
        String(newPassword || "");

      setIsLoading(true);

      try {
        const response =
          await fetch(
            ENDPOINT_RESET_PASSWORD,
            {
              method: "POST",
              headers: {
                "Content-Type":
                  "application/json",
                Accept:
                  "application/json",
              },
              body:
                JSON.stringify({
                  email,
                  novaSenha:
                    passwordToSend,
                  resetToken,
                }),
            }
          );

        const payload =
          await readApiPayload(
            response
          );

        if (!response.ok) {
          const resetError =
            new Error(
              getResetErrorMessage(
                payload,
                response.status
              )
            );

          resetError.status =
            response.status;

          throw resetError;
        }

        clearResetFlow();

        setNewPassword("");
        setConfirmPassword("");

        toast.success({
          title:
            "Senha atualizada",
          message:
            payload?.message ||
            "Sua nova senha foi cadastrada com sucesso.",
        });

        navigate(
          "/password-reset-success",
          {
            replace: true,
            state: {
              email,
              notice: {
                type:
                  "success",
                title:
                  "Senha redefinida",
                message:
                  "Entre com sua nova senha para acessar o PsyAssist Pro.",
              },
            },
          }
        );
      } catch (error) {
        console.error(
          "Erro ao redefinir senha:",
          error
        );

        toast.error({
          title:
            "Não foi possível redefinir",
          message:
            error?.message ||
            "Verifique sua conexão e tente novamente.",
          duration:
            error?.status === 429
              ? 0
              : 6000,
        });
      } finally {
        setIsLoading(false);
      }
    };

  const handleRestartFlow = () => {
    if (isLoading) return;

    clearResetFlow();

    navigate(
      "/forgot-password",
      {
        replace: true,
      }
    );
  };

  const handlePasswordKeyState =
    (event) => {
      setCapsLockActive(
        Boolean(
          event.getModifierState?.(
            "CapsLock"
          )
        )
      );
    };

  const maskedEmail =
    maskEmail(email);

  return (
    <main className="psy-reset-page">
      <div
        className="psy-reset-background"
        aria-hidden="true"
      >
        <span className="psy-reset-background__orb psy-reset-background__orb--one" />
        <span className="psy-reset-background__orb psy-reset-background__orb--two" />
        <span className="psy-reset-background__grid" />
      </div>

      <div className="psy-reset-layout">
        <aside className="psy-reset-aside">
          <Link
            to="/"
            className="psy-reset-brand"
            aria-label="Voltar para a página inicial"
          >
            <img
              src={Logo}
              alt="PsyAssist Pro"
            />

            <div>
              <strong>
                PsyAssist Pro
              </strong>

              <span>
                Proteção para sua conta
              </span>
            </div>
          </Link>

          <div className="psy-reset-aside__content">
            <span className="psy-reset-kicker">
              <Sparkles size={15} />
              Última etapa
            </span>

            <h1>
              Crie uma nova senha para recuperar
              seu acesso.
            </h1>

            <p>
              A nova credencial será vinculada
              ao e-mail confirmado na etapa
              anterior do fluxo de recuperação.
            </p>

            <div className="psy-reset-assurances">
              <div className="psy-reset-assurance">
                <span>
                  <KeyRound size={19} />
                </span>

                <div>
                  <strong>
                    Código confirmado
                  </strong>

                  <small>
                    A alteração continua somente
                    depois que o código enviado
                    por e-mail é validado.
                  </small>
                </div>
              </div>

              <div className="psy-reset-assurance">
                <span>
                  <ShieldCheck size={19} />
                </span>

                <div>
                  <strong>
                    Nova credencial
                  </strong>

                  <small>
                    A senha anterior não é
                    exibida, recuperada ou
                    enviada por e-mail.
                  </small>
                </div>
              </div>
            </div>
          </div>

          <div className="psy-reset-security-card">
            <div>
              <LockKeyhole size={19} />
            </div>

            <p>
              <strong>
                Use uma senha exclusiva
              </strong>

              Evite reutilizar a mesma senha de
              e-mail, redes sociais ou outros
              sistemas profissionais.
            </p>
          </div>
        </aside>

        <section className="psy-reset-panel">
          <div className="psy-reset-panel__top">
            <span>
              Redefinição para
            </span>

            <strong>
              {maskedEmail}
            </strong>
          </div>

          <div className="psy-reset-content">
            <header className="psy-reset-header">
              <div
                className="psy-reset-header__icon"
                aria-hidden="true"
              >
                <KeyRound size={22} />
              </div>

              <div>
                <span>
                  Nova credencial
                </span>

                <h2>
                  Defina sua nova senha
                </h2>

                <p>
                  Escolha uma senha segura e
                  diferente das utilizadas
                  anteriormente.
                </p>
              </div>
            </header>

            {!hasValidFlow ? (
              <section
                className="psy-reset-invalid"
                role="alert"
              >
                <div>
                  <AlertTriangle size={21} />
                </div>

                <div>
                  <strong>
                    Autorização não encontrada
                  </strong>

                  <p>
                    A recuperação não possui uma
                    autorização válida. Valide um
                    novo código para continuar.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={
                    handleRestartFlow
                  }
                >
                  Reiniciar recuperação
                </button>
              </section>
            ) : (
              <form
                className="psy-reset-form"
                onSubmit={handleSubmit}
                noValidate
                aria-busy={isLoading}
              >
                <div
                  className={`psy-reset-field ${
                    errors.newPassword
                      ? "psy-reset-field--error"
                      : ""
                  }`}
                >
                  <div className="psy-reset-field__header">
                    <label htmlFor="psy-reset-new-password">
                      Nova senha
                    </label>

                    <span>
                      Obrigatório
                    </span>
                  </div>

                  <div className="psy-reset-control">
                    <LockKeyhole
                      size={18}
                      aria-hidden="true"
                    />

                    <input
                      ref={newPasswordRef}
                      id="psy-reset-new-password"
                      name="new-password"
                      type={
                        showNewPassword
                          ? "text"
                          : "password"
                      }
                      placeholder="Crie uma senha segura"
                      value={newPassword}
                      onChange={(event) => {
                        setNewPassword(
                          event.target.value
                        );

                        clearFieldError(
                          "newPassword"
                        );
                      }}
                      onKeyDown={
                        handlePasswordKeyState
                      }
                      onKeyUp={
                        handlePasswordKeyState
                      }
                      onBlur={() =>
                        setCapsLockActive(
                          false
                        )
                      }
                      autoComplete="new-password"
                      disabled={isLoading}
                      aria-invalid={Boolean(
                        errors.newPassword
                      )}
                      aria-describedby={
                        errors.newPassword
                          ? "psy-reset-new-password-error"
                          : capsLockActive
                            ? "psy-reset-caps-lock"
                            : undefined
                      }
                    />

                    <button
                      type="button"
                      className="psy-reset-password-toggle"
                      onClick={() =>
                        setShowNewPassword(
                          (current) =>
                            !current
                        )
                      }
                      disabled={isLoading}
                      aria-label={
                        showNewPassword
                          ? "Ocultar nova senha"
                          : "Exibir nova senha"
                      }
                    >
                      {showNewPassword ? (
                        <EyeOff size={18} />
                      ) : (
                        <Eye size={18} />
                      )}
                    </button>
                  </div>

                  {errors.newPassword ? (
                    <p
                      id="psy-reset-new-password-error"
                      className="psy-reset-field__error"
                      role="alert"
                    >
                      {errors.newPassword}
                    </p>
                  ) : null}

                  {capsLockActive ? (
                    <p
                      id="psy-reset-caps-lock"
                      className="psy-reset-caps-lock"
                    >
                      Caps Lock está ativado.
                    </p>
                  ) : null}
                </div>

                <div className="psy-reset-password-panel">
                  <div className="psy-reset-password-panel__header">
                    <span>
                      Segurança da senha
                    </span>

                    <strong
                      className={`psy-reset-password-panel__strength psy-reset-password-panel__strength--${passwordScore}`}
                    >
                      {passwordStrengthLabel}
                    </strong>
                  </div>

                  <div className="psy-reset-password-panel__meter">
                    {[1, 2, 3].map(
                      (step) => (
                        <span
                          key={step}
                          className={
                            passwordScore >=
                            step
                              ? "is-active"
                              : ""
                          }
                        />
                      )
                    )}
                  </div>

                  <div className="psy-reset-password-panel__rules">
                    {evaluatedRules.map(
                      (rule) => (
                        <span
                          key={rule.key}
                          className={
                            rule.valid
                              ? "is-valid"
                              : ""
                          }
                        >
                          <Check size={14} />
                          {rule.label}
                        </span>
                      )
                    )}
                  </div>
                </div>

                <div
                  className={`psy-reset-field ${
                    errors.confirmPassword
                      ? "psy-reset-field--error"
                      : ""
                  }`}
                >
                  <div className="psy-reset-field__header">
                    <label htmlFor="psy-reset-confirm-password">
                      Confirmar nova senha
                    </label>

                    <span>
                      Obrigatório
                    </span>
                  </div>

                  <div className="psy-reset-control">
                    <LockKeyhole
                      size={18}
                      aria-hidden="true"
                    />

                    <input
                      id="psy-reset-confirm-password"
                      name="confirm-password"
                      type={
                        showConfirmPassword
                          ? "text"
                          : "password"
                      }
                      placeholder="Repita a nova senha"
                      value={confirmPassword}
                      onChange={(event) => {
                        setConfirmPassword(
                          event.target.value
                        );

                        clearFieldError(
                          "confirmPassword"
                        );
                      }}
                      autoComplete="new-password"
                      disabled={isLoading}
                      aria-invalid={Boolean(
                        errors.confirmPassword
                      )}
                      aria-describedby={
                        errors.confirmPassword
                          ? "psy-reset-confirm-password-error"
                          : undefined
                      }
                    />

                    <button
                      type="button"
                      className="psy-reset-password-toggle"
                      onClick={() =>
                        setShowConfirmPassword(
                          (current) =>
                            !current
                        )
                      }
                      disabled={isLoading}
                      aria-label={
                        showConfirmPassword
                          ? "Ocultar confirmação da senha"
                          : "Exibir confirmação da senha"
                      }
                    >
                      {showConfirmPassword ? (
                        <EyeOff size={18} />
                      ) : (
                        <Eye size={18} />
                      )}
                    </button>
                  </div>

                  {errors.confirmPassword ? (
                    <p
                      id="psy-reset-confirm-password-error"
                      className="psy-reset-field__error"
                      role="alert"
                    >
                      {errors.confirmPassword}
                    </p>
                  ) : confirmPassword ? (
                    <p
                      className={`psy-reset-match ${
                        passwordsMatch
                          ? "psy-reset-match--success"
                          : "psy-reset-match--warning"
                      }`}
                    >
                      {passwordsMatch
                        ? "As senhas coincidem."
                        : "As senhas ainda não coincidem."}
                    </p>
                  ) : null}
                </div>

                <div className="psy-reset-info">
                  <CheckCircle2 size={18} />

                  <p>
                    <strong>
                      Após a confirmação
                    </strong>

                    Você será direcionado para
                    concluir o fluxo e entrar
                    novamente com a nova senha.
                  </p>
                </div>

                <button
                  type="submit"
                  className="psy-reset-primary"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <LoaderCircle
                        size={18}
                        className="psy-reset-spinner"
                      />

                      Atualizando senha...
                    </>
                  ) : (
                    <>
                      Salvar nova senha
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>

                <button
                  type="button"
                  className="psy-reset-secondary"
                  onClick={
                    handleRestartFlow
                  }
                  disabled={isLoading}
                >
                  <ArrowLeft size={18} />
                  Cancelar recuperação
                </button>
              </form>
            )}

            <section className="psy-reset-support">
              <ShieldCheck size={18} />

              <div>
                <strong>
                  Não solicitou esta alteração?
                </strong>

                <span>
                  Cancele o processo e não
                  compartilhe códigos ou links
                  recebidos por e-mail.
                </span>
              </div>
            </section>
          </div>

          <footer className="psy-reset-footer">
            <LockKeyhole size={14} />

            <p>
              A senha é enviada somente para a
              API e não é armazenada no
              navegador por esta tela.
            </p>
          </footer>
        </section>
      </div>
    </main>
  );
};

export default ResetPasswordScreen;
